package com.elan.edubridge.shared.data.repository

import com.elan.edubridge.shared.data.datasource.AcademyCommandDataSource
import com.elan.edubridge.shared.domain.model.AcademyApplicationRequest
import com.elan.edubridge.shared.domain.model.AcceptInvitationResult
import com.elan.edubridge.shared.domain.model.AmendAttendanceRequest
import com.elan.edubridge.shared.domain.model.BatchCommandResult
import com.elan.edubridge.shared.domain.model.CalendarEventResult
import com.elan.edubridge.shared.domain.model.CommandResult
import com.elan.edubridge.shared.domain.model.CommentResult
import com.elan.edubridge.shared.domain.model.ContentReportRequest
import com.elan.edubridge.shared.domain.model.ContentReportResolution
import com.elan.edubridge.shared.domain.model.ContentReportResult
import com.elan.edubridge.shared.domain.model.CreateAcademyEventRequest
import com.elan.edubridge.shared.domain.model.CreateClassRequest
import com.elan.edubridge.shared.domain.model.CreateCommentRequest
import com.elan.edubridge.shared.domain.model.CreateInvitationRequest
import com.elan.edubridge.shared.domain.model.AssignmentAttachmentTicket
import com.elan.edubridge.shared.domain.model.CreateAssignmentRequest
import com.elan.edubridge.shared.domain.model.CreateNoticeRequest
import com.elan.edubridge.shared.domain.model.CreatePersonalEventRequest
import com.elan.edubridge.shared.domain.model.CreatePostAttachmentUploadRequest
import com.elan.edubridge.shared.domain.model.CreatePostRequest
import com.elan.edubridge.shared.domain.model.CreateStudentRequest
import com.elan.edubridge.shared.domain.model.InvitationResult
import com.elan.edubridge.shared.domain.model.InvitationPreviewResult
import com.elan.edubridge.shared.domain.model.JoinCodePreviewResult
import com.elan.edubridge.shared.domain.model.JoinCodeResult
import com.elan.edubridge.shared.domain.model.NoticeResult
import com.elan.edubridge.shared.domain.model.NotificationSummary
import com.elan.edubridge.shared.domain.model.PostAttachmentDownloadTicket
import com.elan.edubridge.shared.domain.model.PostAttachmentUploadTicket
import com.elan.edubridge.shared.domain.model.PostResult
import com.elan.edubridge.shared.domain.model.QrSessionResult
import com.elan.edubridge.shared.domain.model.QrTokenResult
import com.elan.edubridge.shared.domain.model.RecordAttendanceRequest
import com.elan.edubridge.shared.domain.model.ScanRequest
import com.elan.edubridge.shared.domain.model.ScanResult
import com.elan.edubridge.shared.domain.model.SubmitJoinApplicationResult
import com.elan.edubridge.shared.domain.model.UpdateAcademyEventRequest
import com.elan.edubridge.shared.domain.model.UpdateAcademyRequest
import com.elan.edubridge.shared.domain.model.UpdateAssignmentRequest
import com.elan.edubridge.shared.domain.model.UpdateClassRequest
import com.elan.edubridge.shared.domain.model.UpdateNoticeRequest
import com.elan.edubridge.shared.domain.model.UpdatePersonalEventRequest
import com.elan.edubridge.shared.domain.model.UpdatePostRequest
import com.elan.edubridge.shared.domain.model.UpdateStudentRequest
import com.elan.edubridge.shared.domain.repository.AcademyCommandRepository

class AcademyCommandRepositoryImpl(
    private val dataSource: AcademyCommandDataSource,
) : AcademyCommandRepository {
    override suspend fun submitAcademyApplication(
        request: AcademyApplicationRequest,
    ): CommandResult = dataSource.submitAcademyApplication(request)

    override suspend fun approveAcademyApplication(applicationId: String): CommandResult =
        dataSource.approveAcademyApplication(applicationId)

    override suspend fun updateAcademy(request: UpdateAcademyRequest): CommandResult =
        dataSource.updateAcademy(request)

    override suspend fun createClass(request: CreateClassRequest): CommandResult =
        dataSource.createClass(request)

    override suspend fun updateClass(request: UpdateClassRequest): CommandResult =
        dataSource.updateClass(request)

    override suspend fun createStudent(request: CreateStudentRequest): CommandResult =
        dataSource.createStudent(request)

    override suspend fun updateStudent(request: UpdateStudentRequest): CommandResult =
        dataSource.updateStudent(request)

    override suspend fun createEnrollment(
        academyId: String,
        classId: String,
        studentId: String,
    ): CommandResult = dataSource.createEnrollment(academyId, classId, studentId)

    override suspend fun createInvitation(request: CreateInvitationRequest): InvitationResult =
        dataSource.createInvitation(request)

    override suspend fun previewInvitation(invitationCode: String): InvitationPreviewResult =
        dataSource.previewInvitation(invitationCode)

    override suspend fun acceptInvitation(invitationCode: String): AcceptInvitationResult =
        dataSource.acceptInvitation(invitationCode)

    override suspend fun assignTeacherToClass(
        academyId: String,
        classId: String,
        teacherUserId: String,
    ): CommandResult = dataSource.assignTeacherToClass(academyId, classId, teacherUserId)

    override suspend fun revokeGuardianLink(academyId: String, linkId: String): CommandResult =
        dataSource.revokeGuardianLink(academyId, linkId)

    override suspend fun recordAttendance(request: RecordAttendanceRequest): CommandResult =
        dataSource.recordAttendance(request)

    override suspend fun recordAttendanceBatch(
        requests: List<RecordAttendanceRequest>,
    ): BatchCommandResult = dataSource.recordAttendanceBatch(requests)

    override suspend fun amendAttendance(request: AmendAttendanceRequest): CommandResult =
        dataSource.amendAttendance(request)

    override suspend fun createQrSession(academyId: String, label: String): QrSessionResult =
        dataSource.createQrSession(academyId, label)

    override suspend fun rotateQrToken(academyId: String, sessionId: String): QrTokenResult =
        dataSource.rotateQrToken(academyId, sessionId)

    override suspend fun scanAttendance(request: ScanRequest): ScanResult =
        dataSource.scanAttendance(request)

    override suspend fun endQrSession(academyId: String, sessionId: String): CommandResult =
        dataSource.endQrSession(academyId, sessionId)

    override suspend fun createAssignment(request: CreateAssignmentRequest): CommandResult =
        dataSource.createAssignment(request)

    override suspend fun updateAssignment(request: UpdateAssignmentRequest): CommandResult =
        dataSource.updateAssignment(request)

    override suspend fun deleteAssignment(academyId: String, assignmentId: String): CommandResult =
        dataSource.deleteAssignment(academyId, assignmentId)

    override suspend fun attachAssignmentUpload(
        academyId: String,
        assignmentId: String,
        uploadId: String,
        fileName: String,
    ): CommandResult =
        dataSource.attachAssignmentUpload(academyId, assignmentId, uploadId, fileName)

    override suspend fun removeAssignmentAttachment(
        academyId: String,
        assignmentId: String,
        attachmentId: String,
    ): CommandResult =
        dataSource.removeAssignmentAttachment(academyId, assignmentId, attachmentId)

    override suspend fun getAssignmentAttachmentUrl(
        academyId: String,
        assignmentId: String,
        attachmentId: String,
    ): AssignmentAttachmentTicket =
        dataSource.getAssignmentAttachmentUrl(academyId, assignmentId, attachmentId)

    override suspend fun createNotice(request: CreateNoticeRequest): NoticeResult =
        dataSource.createNotice(request)

    override suspend fun updateNotice(request: UpdateNoticeRequest): CommandResult =
        dataSource.updateNotice(request)

    override suspend fun deleteNotice(academyId: String, noticeId: String): CommandResult =
        dataSource.deleteNotice(academyId, noticeId)

    override suspend fun createPost(request: CreatePostRequest): PostResult =
        dataSource.createPost(request)

    override suspend fun updatePost(request: UpdatePostRequest): CommandResult =
        dataSource.updatePost(request)

    override suspend fun deletePost(academyId: String, postId: String): CommandResult =
        dataSource.deletePost(academyId, postId)

    override suspend fun createComment(request: CreateCommentRequest): CommentResult =
        dataSource.createComment(request)

    override suspend fun deleteComment(
        academyId: String,
        postId: String,
        commentId: String,
    ): CommandResult = dataSource.deleteComment(academyId, postId, commentId)

    override suspend fun createPostAttachmentUploadUrl(
        request: CreatePostAttachmentUploadRequest,
    ): PostAttachmentUploadTicket = dataSource.createPostAttachmentUploadUrl(request)

    override suspend fun getPostAttachmentUrl(
        academyId: String,
        postId: String,
        attachmentId: String,
    ): PostAttachmentDownloadTicket =
        dataSource.getPostAttachmentUrl(academyId, postId, attachmentId)

    override suspend fun reportContent(request: ContentReportRequest): ContentReportResult =
        dataSource.reportContent(request)

    override suspend fun resolveContentReport(
        academyId: String,
        reportId: String,
        resolution: ContentReportResolution,
        note: String?,
    ): CommandResult =
        dataSource.resolveContentReport(academyId, reportId, resolution, note)

    override suspend fun createAcademyEvent(
        request: CreateAcademyEventRequest,
    ): CalendarEventResult = dataSource.createAcademyEvent(request)

    override suspend fun updateAcademyEvent(
        request: UpdateAcademyEventRequest,
    ): CommandResult = dataSource.updateAcademyEvent(request)

    override suspend fun deleteAcademyEvent(academyId: String, eventId: String): CommandResult =
        dataSource.deleteAcademyEvent(academyId, eventId)

    override suspend fun createPersonalEvent(
        request: CreatePersonalEventRequest,
    ): CalendarEventResult = dataSource.createPersonalEvent(request)

    override suspend fun updatePersonalEvent(
        request: UpdatePersonalEventRequest,
    ): CommandResult = dataSource.updatePersonalEvent(request)

    override suspend fun deletePersonalEvent(eventId: String): CommandResult =
        dataSource.deletePersonalEvent(eventId)

    override suspend fun notificationSummary(academyId: String): NotificationSummary =
        dataSource.notificationSummary(academyId)

    override suspend fun issueAcademyJoinCode(academyId: String): JoinCodeResult =
        dataSource.issueAcademyJoinCode(academyId)

    override suspend fun rotateAcademyJoinCode(academyId: String): JoinCodeResult =
        dataSource.rotateAcademyJoinCode(academyId)

    override suspend fun previewAcademyJoinCode(joinCode: String): JoinCodePreviewResult =
        dataSource.previewAcademyJoinCode(joinCode)

    override suspend fun submitAcademyJoinApplication(
        joinCode: String,
        consentAgreed: Boolean,
    ): SubmitJoinApplicationResult =
        dataSource.submitAcademyJoinApplication(joinCode, consentAgreed)

    override suspend fun cancelAcademyJoinApplication(academyId: String): CommandResult =
        dataSource.cancelAcademyJoinApplication(academyId)

    override suspend fun approveAcademyJoinApplication(
        academyId: String,
        applicantUid: String,
        studentId: String?,
    ): CommandResult =
        dataSource.approveAcademyJoinApplication(academyId, applicantUid, studentId)

    override suspend fun rejectAcademyJoinApplication(
        academyId: String,
        applicantUid: String,
        blockFuture: Boolean,
    ): CommandResult =
        dataSource.rejectAcademyJoinApplication(academyId, applicantUid, blockFuture)

    override suspend fun unblockAcademyJoinApplication(
        academyId: String,
        applicantUid: String,
    ): CommandResult =
        dataSource.unblockAcademyJoinApplication(academyId, applicantUid)
}
